import { useMemo } from 'react';
import { Commerce } from '@interfaces/general';

/**
 * Resultado de validación para cada configuración
 */
interface ConfigValidationResult {
  isComplete: boolean;
  priority: 'high' | 'medium' | 'low';
  missingFields: string[];
}

/**
 * Resultado completo de validación de Setup
 */
export interface SetupValidationResult {
  whatsapp: ConfigValidationResult;
  address: ConfigValidationResult;
  deliveryMethods: ConfigValidationResult;
  paymentMethods: ConfigValidationResult;
  schedules: ConfigValidationResult;
  completionPercentage: number;
  shouldShowSetupPage: boolean;
  allConfigsComplete: boolean;
}

/**
 * Helper: Verifica si al menos un método de pago está activo
 * @param {Object} paymentMethods - Objeto con los métodos de pago disponibles
 * @returns {boolean} True si al menos un método de pago está activo
 */
const hasAtLeastOnePaymentMethod = (paymentMethods?: {
  efectivo: boolean;
  tarjeta: boolean;
  transferencia: boolean;
  enlace_pago: boolean;
  payment_provider: boolean;
}): boolean => {
  if (!paymentMethods) return false;
  return Object.values(paymentMethods).some((method) => method === true);
};

/**
 * Helper: Valida que todos los métodos de entrega activos tengan al menos un método de pago
 * @param {Commerce} commerce - Objeto completo del comercio
 * @returns {boolean} True si todos los métodos activos tienen al menos un método de pago
 */
const validatePaymentMethods = (commerce: Commerce): boolean => {
  const activeDeliveryTypes = [
    { active: commerce.activar_envio_domicilio, payments: commerce.metodos_pago_envio_domicilio },
    { active: commerce.activar_pasar_recoger, payments: commerce.metodos_pago_pasar_recoger },
    { active: commerce.activar_comer_restaurant, payments: commerce.metodos_pago_comer_restaurant },
  ].filter((type) => type.active);

  // Si no hay métodos de entrega activos, no está completo
  if (activeDeliveryTypes.length === 0) return false;

  // Todos los métodos activos deben tener al menos un método de pago
  return activeDeliveryTypes.every((type) => hasAtLeastOnePaymentMethod(type.payments));
};

/**
 * Hook para validar el estado de completitud de las configuraciones del Setup
 *
 * @param {Commerce} commerce - Objeto completo del comercio desde Redux
 * @returns {SetupValidationResult} Resultado de validación con estado de cada configuración
 *
 * @example
 * ```tsx
 * const { commerce } = useCommerce();
 * const setupStatus = useSetupValidation(commerce);
 *
 * if (!setupStatus.whatsapp.isComplete) {
 *   // Mostrar warning de WhatsApp
 * }
 *
 * console.log(`Progreso: ${setupStatus.completionPercentage}%`);
 * ```
 */
export const useSetupValidation = (commerce: Commerce): SetupValidationResult => {
  return useMemo(() => {
    // ========================================
    // 1. VALIDACIÓN DE WHATSAPP
    // ========================================
    const isWhatsAppComplete = Boolean(
      commerce.telefono && commerce.country_code && commerce.telefono.toString().length > 0,
    );

    // ========================================
    // 2. VALIDACIÓN DE DIRECCIÓN
    // ========================================
    const isAddressComplete = Boolean(
      commerce.address?.fullAddress &&
        commerce.address?.latitude &&
        commerce.address?.longitude &&
        commerce.address?.state &&
        commerce.address?.city &&
        commerce.address?.street &&
        commerce.address?.postalCode &&
        commerce.address?.exteriorNumber,
    );

    // ========================================
    // 3. VALIDACIÓN DE MÉTODOS DE ENTREGA
    // ========================================
    const isDeliveryMethodsComplete = Boolean(
      commerce.activar_envio_domicilio || commerce.activar_pasar_recoger || commerce.activar_comer_restaurant,
    );

    // ========================================
    // 4. VALIDACIÓN DE MÉTODOS DE PAGO
    // ========================================
    const isPaymentMethodsComplete = validatePaymentMethods(commerce);

    // ========================================
    // 5. VALIDACIÓN DE HORARIOS
    // ========================================
    const isSchedulesComplete = Boolean(
      commerce.horarios &&
        commerce.horarios.length > 0 &&
        commerce.horarios.some((h) => h.horario_apertura && h.horario_cierre),
    );

    // ========================================
    // CÁLCULO DE PORCENTAJE DE COMPLETITUD
    // ========================================
    // Configuraciones de alta prioridad (peso: 1.5 cada una)
    const highPriorityConfigs = [
      isWhatsAppComplete,
      isAddressComplete,
      isDeliveryMethodsComplete,
      isPaymentMethodsComplete,
    ];

    // Configuraciones de media prioridad (peso: 1.0)
    const mediumPriorityConfigs = [isSchedulesComplete];

    // Calcular total ponderado
    const highPriorityWeight = 1.5;
    const mediumPriorityWeight = 1.0;

    const highPriorityCompleted = highPriorityConfigs.filter(Boolean).length * highPriorityWeight;
    const mediumPriorityCompleted = mediumPriorityConfigs.filter(Boolean).length * mediumPriorityWeight;

    const totalWeight =
      highPriorityConfigs.length * highPriorityWeight + mediumPriorityConfigs.length * mediumPriorityWeight;

    const completionPercentage = ((highPriorityCompleted + mediumPriorityCompleted) / totalWeight) * 100;

    // ========================================
    // RESULTADO FINAL
    // ========================================
    const allConfigsComplete = completionPercentage === 100;

    return {
      whatsapp: {
        isComplete: isWhatsAppComplete,
        priority: 'high',
        missingFields: !isWhatsAppComplete ? ['telefono', 'country_code'] : [],
      },
      address: {
        isComplete: isAddressComplete,
        priority: 'high',
        missingFields: !isAddressComplete
          ? ['address.fullAddress', 'address.latitude', 'address.longitude', 'address.state', 'address.city']
          : [],
      },
      deliveryMethods: {
        isComplete: isDeliveryMethodsComplete,
        priority: 'high',
        missingFields: !isDeliveryMethodsComplete
          ? ['activar_envio_domicilio', 'activar_pasar_recoger', 'activar_comer_restaurant']
          : [],
      },
      paymentMethods: {
        isComplete: isPaymentMethodsComplete,
        priority: 'high',
        missingFields: !isPaymentMethodsComplete ? ['metodos_pago_*'] : [],
      },
      schedules: {
        isComplete: isSchedulesComplete,
        priority: 'high',
        missingFields: !isSchedulesComplete ? ['horarios'] : [],
      },
      completionPercentage: Math.min(completionPercentage, 100),
      shouldShowSetupPage: !allConfigsComplete,
      allConfigsComplete,
    };
  }, [
    commerce.telefono,
    commerce.country_code,
    commerce.address,
    commerce.activar_envio_domicilio,
    commerce.activar_pasar_recoger,
    commerce.activar_comer_restaurant,
    commerce.metodos_pago_envio_domicilio,
    commerce.metodos_pago_pasar_recoger,
    commerce.metodos_pago_comer_restaurant,
    commerce.horarios,
  ]);
};
